<template>
  <ExpandableSettingsItem :title="$t('message.NAVIGATION_MENU')">
    <div>
      <div class="generic-background">
        <div v-if="isDefaultNavList" class="navigation-settings">
          <Checkbox :value="false" :title="$t('message.NAVIGATION_CUSTOMIZE')" @input="initializeDefaultNavList()" />
        </div>
        <div v-else class="navigation-settings">
          <Draggable
            v-model="navItemList"
            :animation="200"
            :disabled="navItemListLoading"
            @update="onOrderChange"
          >
            <div
              v-for="item in navItemList"
              :key="item.id"
              class="nav-setting-item generic-border"
            >
              <DraggingIndication />
              <div class="nav-setting-item__details">
                <EditableLabel
                  v-model="item.name"
                  :legend="$t('message.NAME')"
                  :placeholder="$t(getDefaultName(item.pageId))"
                  use-textarea
                  @input="updateNavItem(item)"
                />
                <PagePicker
                  :value="item"
                  no-meeting-pages
                  setting-pages
                  see-page
                  :disabled="item.locked"
                  @input="updateNavItem($event, 'pageId')"
                />
              </div>
              <div class="nav-setting-item__options">
                <div class="nav-setting-item__options__wrapper">
                  <Checkbox
                    v-model="item.isTitleFull"
                    :title="$t('message.NAVIGATION_DISPLAY_NAME')"
                    class="team-name nav-setting-item__full-title"
                    stop-propagation
                    no-animation
                    @input="updateNavItem(item)"
                  />
                  <br>
                  <button
                    class="
                      generic-border
                      generic-border--text-btn
                      generic-border--gray-border
                      nav-setting-item__options__color-button
                    "
                    @click="openPicker(item)"
                  >
                    <Icon>{{ colorPickerMedium }}</Icon>
                    {{ $t("message.CFG_PAGE_COLOR") }}
                    <span
                      :style="backgroundPreviewStyle(item.backgroundColor)"
                      class="nav-setting-item__options__color-button__preview"
                    />
                  </button>
                </div>
                <div
                  v-if="item.locked"
                  v-quick-title="$t('message.NAVIGATION_LOCKED')"
                  class="nav-setting-item__locked"
                >
                  <img class="f-w f-h" src="@/assets/img/lock-medium-gray.png" alt="">
                </div>
                <button
                  v-else
                  class="generic-border generic-border--text-btn generic-border--clickable generic-border--red"
                  @click="deleteNavItem(item.id)"
                >
                  <Icon>{{ deleteMedium }}</Icon>
                  <span>{{ $t('message.DELETE') }}</span>
                </button>
              </div>
            </div>
          </Draggable>
          <div
            class="generic-border generic-border--text-btn v-centerer navigation-settings__add"
            @click="createNavItem"
          >
            <div class="generic-border__left">
              <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
              <span>{{ $t("message.ADD_A_LINK") }}</span>
            </div>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </div>

          <button
            class="
              generic-border
              generic-border--text-btn
              generic-border--clickable
              generic-border--red
              navigation-settings__reset
            "
            @click="onClickResetDefault"
          >
            <Icon>{{ cancelMedium }}</Icon>
            <span>{{ $t('message.RESET_DEFAULT') }}</span>
          </button>

          <!-- Color selection in a modal -->
          <Modal v-model="colorModal.isOpened">
            <div class="navigation-settings__color-modal">
              <div class="navigation-settings__color-modal__header">
                <h2 class="navigation-settings__color-modal__title basic-sub-title">
                  {{ `${$t('message.SELECT_COLOR')}` }}
                </h2>
              </div>
              <SColorPicker
                v-model="colorModal.item.backgroundColor"
                @input="updateNavItem(colorModal.item)"
              />
            </div>
          </Modal>
        </div>
      </div>
    </div>
  </ExpandableSettingsItem>
</template>

<script>
import { cloneDeep } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
import Draggable from 'vuedraggable';
// Import components
import Checkbox from '@/components/checkbox.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import PagePicker from '@/components/page-picker.vue';
import SColorPicker from '@/components/s-color-picker.vue';
// Import Vuex utils
import { GET_NAVIGATION_LIST } from '@/store/modules/navigation/mutation-types';
import {
  FETCH_NAVIGATION_LIST,
  SET_NAVIGATION_ITEM,
  DELETE_NAVIGATION_ITEM,
  RESET_NAVIGATION_LIST,
} from '@/store/modules/navigation/action-types';
// Import utils
import SPage from '@/utils/classes/SPage';
import { NAV_LIST_DEFAULT } from '@/utils/navigation-constants';
import { calert } from '@/utils/main';
// Import assets
import {
  addExtraBold,
  arrowDownLine,
  cancelMedium,
  colorPickerMedium,
  deleteMedium,
} from '@/assets/icons';

const { mapActions, mapState } = createNamespacedHelpers('navigation');

export default {
  name: 'NavigationSettings',

  components: {
    Checkbox,
    Draggable,
    DraggingIndication,
    EditableLabel,
    ExpandableSettingsItem,
    Icon,
    Modal,
    PagePicker,
    SColorPicker,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      cancelMedium,
      colorPickerMedium,
      deleteMedium,
      navItemList: [],
      colorModal: {
        isOpened: false,
        item: {},
      },
      isTitleFull: false,
      cancelTokenPerItemId: {},
    };
  },

  computed: {
    ...mapState({
      navItemListData: state => state[GET_NAVIGATION_LIST.stateKey] || [],
      navItemListLoading: state => state[GET_NAVIGATION_LIST.loadingKey],
    }),
    isDefaultNavList() {
      return this.navItemList.length === 0;
    },
  },

  watch: {
    navItemListLoading(newValue) {
      // Keep client list in sync with the server list
      if (newValue === false) {
        this.navItemList = cloneDeep(this.navItemListData);
      }
    },
  },

  created() {
    this.FETCH_NAVIGATION_LIST();
  },

  methods: {
    ...mapActions({
      FETCH_NAVIGATION_LIST,
      SET_NAVIGATION_ITEM,
      DELETE_NAVIGATION_ITEM,
      RESET_NAVIGATION_LIST,
    }),
    /**
     * Defines the background of the color picker preview, if the background is transparent, add grey border
     */
    backgroundPreviewStyle(color) {
      const style = {
        background: color || 'linear-gradient(to top right, #fff calc(50% - 1px), #aaa, #fff calc(50% + 1px))',
        border: color ? '' : '1px solid #aaa',
      };
      return style;
    },
    /** Create a new navigation item on the server */
    createNavItem() {
      const max = this.navItemList.reduce((max, item) => max > item.orderKey ? max : item.orderKey, 0);
      this.SET_NAVIGATION_ITEM({ items: [{ orderKey: max }] });
    },
    /** Send changes of a navigation item to the server */
    updateNavItem(newItem, fieldEdited) {
      if (this.cancelTokenPerItemId[`${newItem.id}`]) {
        this.cancelTokenPerItemId[`${newItem.id}`]();
      }
      const item = fieldEdited === 'pageId' ? this.handleCfgPageName(newItem) : newItem;
      this.SET_NAVIGATION_ITEM({
        items: [item],
        cancelCallback: (cancel) => {
          this.cancelTokenPerItemId[`${newItem.id}`] = cancel;
        },
      });
    },
    /**
     * Delete navigation item from the server
     * @param {Number} itemId - ID of the navigation item
     */
    deleteNavItem(itemId) {
      this.DELETE_NAVIGATION_ITEM({ id: itemId });
    },
    /**
     * Get i18n value of a page ID
     * @param {Number} pageId - Page ID as defined in SPage classe
     * @return {String} i18n value corresponding to the given pageId
     */
    getDefaultName(pageId) {
      const page = Object.values(SPage).find(p => p.id === pageId);
      return page ? page.i18nName : 'message.NONE';
    },
    /*
     * If page is a configurable-page,
     * set item name to its corresponding configurable-page name
     * @param {Object} item - Nav Item data to send to a request
     * @return {Object} Original or modified Nav item
     */
    handleCfgPageName(item) {
      if (item.pageId !== SPage.CFG_PAGE.id) {
        return item;
      }
      const cfgPage = this.$store.getters.getCfgPageFromId(item.itemId);
      if (cfgPage) {
        item.name = cfgPage.name;
      }
      return item;
    },
    /** Save in the database the default navigation list */
    initializeDefaultNavList() {
      this.SET_NAVIGATION_ITEM({
        items: NAV_LIST_DEFAULT.map((item, index) => Object.assign({}, item, { id: undefined, orderKey: index })),
        resetNavItems: true,
      });
    },
    /** Change the order of the navigation items */
    onOrderChange() {
      const changedItems = [];
      this.navItemList.forEach((item, idx) => {
        if (item.orderKey !== idx) {
          changedItems.push({ ...item, orderKey: idx });
        }
      });
      this.SET_NAVIGATION_ITEM({ items: changedItems });
    },
    /** Reset the navigation menu to the default items */
    onClickResetDefault() {
      calert(this.$t('message.NAVIGATION_RESET_CONFIRM'), [
        {
          title: this.$t('message.RESET'),
          sClass: 'red',
          action: () => {
            this.RESET_NAVIGATION_LIST();
          },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    openPicker(item) {
      /* This will open the modal with the actual color selected.
      If the user has never selected a background color before, it selects the transparent color. */
      this.colorModal.isOpened = true;
      this.colorModal.item = item;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.sd-overflow div.navigation-settings {

  .navigation-settings__add {
    margin-top: .7rem;
    max-width: 25rem;

    &.generic-border--text-btn {
      background: #fff;
    }
  }

  .navigation-settings__reset {
    margin-top: 2.5rem;
  }

  .modal.dark-overlay .dark-overlay-content {
    width: auto;
  }

  .navigation-settings__color-modal {
    padding: 0 1rem;
    box-sizing: border-box;
    margin-right: 3rem;

    &__title {

    }

    .color-picker {
      width: 22.5rem;

      > div {
        margin: 0 0.5rem 0.5rem 0;
        width: 2rem;
        padding-bottom: 2rem;
        box-sizing: border-box;
      }
    }
  }
}

.nav-setting-item {
  box-sizing: border-box;
  margin-top: 10px;
  position: relative;
  width: 100%;
  padding: 1em;
  padding-left: 1.8rem;
  display: flex;

  .dragging-indication {
    position: absolute;
    left: .5rem;
    top: 50%;
    transform: translateY(-50%) scale(0.8);
  }

  &__details {
    flex: 1;
    min-width: 0px;

    .page-picker {
      margin-top: 1rem;
    }
  }

  &__options {
    flex: 0 0 auto;
    display: flex;
    flex-flow: column;
    align-items: flex-end;
    margin-left: 2rem;

    &__wrapper {
      flex: 1;
      min-height: 0px;
    }

    &__color-button {
      margin: 0.7rem 0;

      &__preview {
        width: 8px;
        height: 8px;
        margin-left: 0.3em;
        border-radius: 2px;
      }
    }
  }

  &__locked {
    width: 1.2em;
  }
}

@media (max-width: $smartphone-portrait) {
  .nav-setting-item {
    flex-flow: column;

    &__options {
      margin-top: 1rem;
      margin-left: 0;
      align-items: flex-start;
    }
  }
}
</style>
